import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { EnvironmentService } from '../environment';

// We type lazy to avoid hard dependency at compile-time if package is missing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SplitFactory: any;

@Injectable({ providedIn: 'root' })
export class SplitService {
  private readonly ready$ = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private client: any = null;
  private readonly defaultKey = 'anonymous';
  private initialized = false;

  private ensureInit(): void {
    if (!this.initialized) {
      this.initialized = true;
      // Fire-and-forget async init
      void this.init();
    }
  }

  constructor(
    private readonly envService: EnvironmentService,
    private readonly zone: NgZone
  ) {}

  private async init() {
    try {
      let splitCfg = this.envService.splitConfig;
      if (!splitCfg?.authorizationKey && this.envService.environment$) {
        await firstValueFrom(
          this.envService.environment$.pipe(
            filter((env) => !!env),
            take(1)
          )
        );
        splitCfg = this.envService.splitConfig;
      }
      const authKey = splitCfg?.authorizationKey;
      if (!authKey) {
        // No key configured; operate in fallback mode (always ready, no client)
        this.zone.run(() => this.ready$.next(true));
        return;
      }

      const getGlobalFactory = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w: any = window as any;
        const glb = w?.splitio;
        if (!glb) return undefined;
        return glb.SplitFactory || glb;
      };

      // Try global (CDN) first
      SplitFactory = getGlobalFactory();

      // If not present globally, try require
      if (!SplitFactory) {
        console.warn('SplitFactory not available globally;');
      }

      // If still not available, poll for CDN script for up to ~5s
      if (!SplitFactory) {
        await new Promise<void>((resolve) => {
          let attempts = 0;
          const maxAttempts = 50; // 50 * 100ms = 5s
          const iv = setInterval(() => {
            const glb = getGlobalFactory();
            if (glb) {
              SplitFactory = glb;
              clearInterval(iv);
              resolve();
            } else if (++attempts >= maxAttempts) {
              clearInterval(iv);
              resolve();
            }
          }, 100);
        });
      }

      if (!SplitFactory) {
        // SDK not available; fail open
        this.zone.run(() => this.ready$.next(true));
        this.client = null;
        return;
      }

      const factory = SplitFactory({
        core: {
          authorizationKey: authKey,
          key: this.defaultKey,
          trafficType: splitCfg?.trafficType || 'user',
        },
        scheduler: {
          featuresRefreshRate: 15,
          segmentsRefreshRate: 60,
          metricsRefreshRate: 120,
        },
        debug: !this.envService.isProduction,
      });
      this.client = factory.client();

      this.client.on(this.client.Event.SDK_READY, () => {
        this.zone.run(() => this.ready$.next(true));
      });
      this.client.on(this.client.Event.SDK_READY_FROM_CACHE, () => {
        this.zone.run(() => {
          if (!this.ready$.value) this.ready$.next(true);
        });
      });
      // Re-evaluate flags whenever SDK updates treatments (e.g., when a flag is toggled)
      if (this.client.Event?.SDK_UPDATE) {
        this.client.on(this.client.Event.SDK_UPDATE, () => {
          // Trigger downstream subscribers to recompute treatment values
          this.zone.run(() => this.ready$.next(this.ready$.value));
        });
      }
      // Safety timeout: be ready even if SDK doesn’t emit events (network issues)
      setTimeout(() => {
        this.zone.run(() => {
          if (!this.ready$.value) this.ready$.next(true);
        });
      }, 3000);
    } catch (e) {
      // Fail open: consider ready without SDK
      // eslint-disable-next-line no-console
      console.warn(
        'SplitService initialization failed or SDK unavailable. Falling back to defaults.',
        e
      );
      this.zone.run(() => this.ready$.next(true));
      this.client = null;
    }
  }

  isOn$(
    flagName: string,
    defaultOn = true,
    attributes?: Record<string, unknown>
  ): Observable<boolean> {
    this.ensureInit();
    return this.ready$.pipe(
      map(() => {
        if (!this.client) {
          return defaultOn;
        }
        try {
          const treatment: string = this.client.getTreatment(
            flagName,
            attributes
          );
          if (treatment === 'on') return true;
          if (treatment === 'off') return false;
          // If control or unknown, use default
          return defaultOn;
        } catch (e) {
          return defaultOn;
        }
      }),
      distinctUntilChanged()
    );
  }
}
